import { useCallback, useMemo } from "react";
import { Dayjs } from "dayjs";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { AreaMaterialSnapshot } from "../types";

type UseAreaMaterialStateArgs = Readonly<{
  videoStart: Dayjs;
  getAreaMaterialMappingAtTime: (timestamp: Dayjs) => Record<string, string>;
  updateAreaMaterialMapping: (
    timestamp: Dayjs,
    area: string,
    material: string | null
  ) => void;
  deleteAreaMaterialSnapshot: (timestamp: Dayjs) => void;
  editingSnapshot: AreaMaterialSnapshot | null;
}>;

type UseAreaMaterialStateResult = Readonly<{
  currentTimestamp: Dayjs;
  currentAreaMaterialMapping: Record<string, string>;
  editingSnapshotCurrentMapping: Record<string, string>;
  handleAreaMaterialUpdate: (area: string, material: string | null) => void;
  handleSnapshotMappingUpdate: (area: string, material: string | null) => void;
}>;

/**
 * Hook for managing area-material mapping state and computed values.
 * Handles current mappings, area colors for mini-map, and update handlers.
 */
export const useAreaMaterialState = ({
  videoStart,
  getAreaMaterialMappingAtTime,
  updateAreaMaterialMapping,
  editingSnapshot,
}: UseAreaMaterialStateArgs): UseAreaMaterialStateResult => {
  const { currentTime } = useVideoControlState();

  const currentTimestamp = useMemo(
    () => videoStart.add(currentTime, "second"),
    [videoStart, currentTime]
  );

  const currentAreaMaterialMapping = useMemo(
    (): Record<string, string> =>
      getAreaMaterialMappingAtTime(currentTimestamp),
    [getAreaMaterialMappingAtTime, currentTimestamp]
  );

  const editingSnapshotCurrentMapping = useMemo((): Record<string, string> => {
    if (!editingSnapshot) return {};
    return getAreaMaterialMappingAtTime(editingSnapshot.timestamp);
  }, [editingSnapshot, getAreaMaterialMappingAtTime]);

  const handleAreaMaterialUpdate = useCallback(
    (area: string, material: string | null) => {
      updateAreaMaterialMapping(currentTimestamp, area, material);
    },
    [updateAreaMaterialMapping, currentTimestamp]
  );

  const handleSnapshotMappingUpdate = useCallback(
    (area: string, material: string | null) => {
      if (editingSnapshot) {
        updateAreaMaterialMapping(editingSnapshot.timestamp, area, material);
      }
    },
    [updateAreaMaterialMapping, editingSnapshot]
  );

  return {
    currentTimestamp,
    currentAreaMaterialMapping,
    editingSnapshotCurrentMapping,
    handleAreaMaterialUpdate,
    handleSnapshotMappingUpdate,
  };
};
